import React, { useCallback, useMemo, useState } from 'react'
import { Button, Box, Typography } from '@mui/material'
import { useModalContext } from '../../contexts/ModalProvider'
import { Alert, useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import setNestedValue from 'lodash.set'
import AlertNotificationTimeOfDayForm from './alertForms/AlertNotificationTimeOfDayForm'
import { NotificationAdd } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import AlertLastChancePhoneNumberInputForm from './alertForms/AlertLastChancePhoneNumberInputForm'
import { Tabs } from '../../layout/MainLayout'
import MultiSlideDialog, { SlideStep } from '../common/MultiSlideDialog'
import { useSavePattern } from '../../hooks/useSavePattern'
import { copyPatternToAccount } from '../../common/utils/copyPatternToAccount'
import { saveNewAlert } from '../../common/utils/saveNewAlert'
import { captureEvent } from '../../posthog'

function CreateAlertModal({ alert: initialAlert }: { alert: Alert }) {
  const { setActiveTab, canCreateAlerts, usermeta, tradingHouse, canViewPrivatePatterns } =
    useApplicationSuiteContext()

  const [alert, setAlert] = useState<Alert>(initialAlert)

  const { isCreateAlertModalOpen, setIsCreateAlertModalOpen } = useModalContext()

  const { forceSave } = useSavePattern(() => {}, usermeta, tradingHouse, canViewPrivatePatterns)

  const handleAlertKeyChange = useCallback((key: string, value: any) => {
    setAlert((prev) => setNestedValue({ ...prev }, key, value))
  }, [])

  const handleAlertCreate = useCallback(async () => {
    if (!canCreateAlerts || !alert.symbol || !alert.pattern) {
      return
    }

    const alertDataToSave = structuredClone(alert)

    const isOwnPattern =
      alert.from === 'pattern' &&
      alertDataToSave.pattern.gcid === usermeta?.gcid &&
      alertDataToSave.pattern.id

    if (!isOwnPattern) {
      const savedPattern = await copyPatternToAccount(
        alertDataToSave.pattern,
        forceSave,
        alertDataToSave.pattern.gcid || (alertDataToSave as any).backtestGcid
      )
      if (savedPattern) {
        alertDataToSave.pattern = savedPattern
      }
    }

    // Keep the persisted alert so the alerts tab selects and displays the
    // same document the card renders.
    const savedAlert = await saveNewAlert(alertDataToSave)
    setAlert(savedAlert)
    captureEvent('alert_created', { source: alert.from || 'new' })
  }, [alert, canCreateAlerts, forceSave, usermeta?.gcid])

  const onClose = useCallback(() => {
    setAlert(initialAlert)
    setIsCreateAlertModalOpen(false)
  }, [initialAlert, setIsCreateAlertModalOpen])

  const goToAlertsTab = useCallback(() => {
    setActiveTab({ tab: Tabs.Alerts, options: { item: alert } })
    onClose()
  }, [alert, setActiveTab, onClose])

  const steps: SlideStep[] = useMemo(
    () => getSteps(initialAlert, alert, handleAlertCreate, goToAlertsTab),
    [initialAlert, alert, handleAlertCreate, goToAlertsTab]
  )

  return (
    <MultiSlideDialog
      open={isCreateAlertModalOpen}
      onClose={onClose}
      title="Set Alert"
      steps={steps}
      maxWidth="sm"
      slideProps={{ alert, onAlertKeyChange: handleAlertKeyChange }}
      actionProps={{ alert, onAlertCreate: handleAlertCreate, goToAlertsTab }}
    />
  )
}

function SetupAlertSlide() {
  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      gap={2}
      px={2}
      alignItems={'center'}
      textAlign={'center'}
    >
      <NotificationAdd sx={{ fontSize: '64px' }} color={'success'} />
      <Typography variant={'h4'} fontWeight={'bold'}>
        Setup Trade Alert
      </Typography>
      <Typography variant={'body2'} color={'textSecondary'}>
        Create alerts based on your strategies. Get instant notifications, keeping you informed
        about potential trade opportunities. This ensures you never miss a moment to execute a
        favorable trade.
      </Typography>
    </Box>
  )
}

function SetupAlertAgreementSlide() {
  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Typography variant={'body1'}>Pattern Alerts Agreement</Typography>
      <Typography variant={'body2'} color={'textSecondary'}>
        By using Pattern Alerts, you acknowledge that you understand and agree to the following:
      </Typography>
      <Typography variant={'body2'} color={'textSecondary'}>
        Use Pattern Alerts at your own risk. When used with proper training they can be a great tool
        to help you identify key setups and movements in the market. Rules and conditions that
        govern a Pattern Alert are the sole responsibility of the Pattern Author. You should test
        and prove a Pattern Alert before employing them with your own trading strategy. Pattern
        Alerts are not trading advice, and you should not rely solely on them for your trade
        decisions. Seek for professional advice on how to properly account for risk and execute
        trades with your broker.By providing your email and/or mobile phone number in the Pattern
        Alerts tool you agree to have GreenChart send you alert notifications. GreenChart is not
        responsible for any charges that your mobile phone carrier may assess for text messaging.
        You can remove text or email notifications at any time in the Alert edit view. GreenChart
        cannot guarantee that notifications will be delivered to your email or mobile phone due to
        possible email or mobile phone provider restrictions. Contact your provider to remove any
        restrictions.
      </Typography>
    </Box>
  )
}

function AlertCreateConfirmationForm() {
  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'column'}
        gap={2}
        px={2}
        alignItems={'center'}
        textAlign={'center'}
      >
        <CheckIcon sx={{ fontSize: '64px' }} color={'success'} />
        <Typography variant={'h4'} fontWeight={'bold'}>
          Setup Complete
        </Typography>
        <Typography variant={'body2'} color={'textSecondary'}>
          Your custom alert has been created! To monitor your new alert go to the charts view.
        </Typography>
      </Box>
    </>
  )
}

function getSteps(
  startingAlert: Alert,
  alert: Alert,
  handleAlertCreate: () => Promise<void>,
  goToAlertsTab: () => void
): SlideStep[] {
  const slides: SlideStep[] = [
    {
      slideComponent: SetupAlertSlide,
      actionsComponent: ({ onNextStep }: any) => (
        <Button onClick={onNextStep} variant="contained" color="success">
          Begin setup
        </Button>
      ),
    },
    {
      slideComponent: SetupAlertAgreementSlide,
      actionsComponent: ({ onNextStep }: any) => (
        <Button onClick={onNextStep} variant="contained" color="success">
          I Agree
        </Button>
      ),
    },
    {
      slideComponent: AlertNotificationTimeOfDayForm,
      slideName: 'Alert Details',
      subtitle: 'Never miss a trade alert by adding when and how you want to be notified.',
      actionsComponent: ({ onPreviousStep, onNextStep }: any) => (
        <Box
          display="flex"
          width="100%"
          height="100%"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button onClick={onPreviousStep} color="success">
            Back
          </Button>
          <Button
            onClick={
              alert.user?.phoneNumber
                ? async () => {
                    await handleAlertCreate()
                    onNextStep()
                  }
                : onNextStep
            }
            variant="contained"
            color="success"
            disabled={!alert.pattern || !alert.symbol}
          >
            Finish Setup
          </Button>
        </Box>
      ),
    },
  ]

  if (!alert.user?.phoneNumber) {
    slides.push({
      slideComponent: AlertLastChancePhoneNumberInputForm,
      slideName: 'Add Text Alerts',
      subtitle:
        'Get notified of trade alerts on the go by adding your phone number for text alerts.',
      actionsComponent: ({ onPreviousStep, onNextStep }: any) => (
        <>
          <Button onClick={onPreviousStep} color="success">
            Back
          </Button>
          <Button
            onClick={async () => {
              await handleAlertCreate()
              onNextStep()
            }}
            variant="contained"
            color="success"
          >
            Skip This Step
          </Button>
        </>
      ),
    })
  }

  slides.push({
    slideComponent: AlertCreateConfirmationForm,
    actionsComponent: () => (
      <Box display="flex" justifyContent="space-between" width="100%" height="100%">
        <Button onClick={goToAlertsTab} variant="contained" sx={{ mx: 'auto' }} color="success">
          Monitor Alert
        </Button>
      </Box>
    ),
  })

  return slides
}

export default CreateAlertModal
